/**
 * One facet of the catalog's filter row - People, Type, Kind, Ship, Tags.
 *
 * A small box in the Sort control's shape - label and chevron - until
 * something is ticked, then the label and what is ticked ("Type · Line",
 * "People · 2") in the accent, with a × that clears it without opening
 * anything. Boxed rather than bare text: as plain words the row read as a
 * caption, and nobody found the filters.
 *
 * **One menu at a time.** A trigger stops its click from reaching the
 * document, so the outside-click listener of an already open filter never
 * heard it and two menus stacked on each other. Opening one now closes
 * whichever was open (`CatalogFilter.current`).
 *
 * The menu is a checklist, because every facet is "any of these" - two people,
 * or line and scatter. Counts come from the tab, not from the filtered grid, so
 * ticking one value does not make the others' numbers jump. Long lists (people
 * on a busy Public tab, ships across a fleet) get a search box of their own.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import type { FilterOption } from './catalog-state.ts';

/** Above this many options the menu gets its own search box. */
const SEARCH_FROM = 7;

@customElement('catalog-filter')
export class CatalogFilter extends LightElement {
  /** The filter whose menu is open, so opening another can close it first. */
  private static current: CatalogFilter | null = null;

  @property() label = '';
  @property({ attribute: false }) options: FilterOption[] = [];
  @property({ attribute: false }) selected: string[] = [];
  @property({ attribute: false }) change: (values: string[]) => void = () => {};

  @state() private open = false;
  @state() private query = '';

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.close();
  }

  private toggleOpen(e: Event): void {
    e.stopPropagation();
    if (this.open) {
      this.close();
      return;
    }
    if (CatalogFilter.current && CatalogFilter.current !== this) CatalogFilter.current.close();
    CatalogFilter.current = this;
    this.open = true;
    this.query = '';
    // Next tick, so the click that opened it does not also close it.
    setTimeout(() => {
      document.addEventListener('click', this.onOutside);
      document.addEventListener('keydown', this.onKey);
    });
  }

  private close = (): void => {
    if (CatalogFilter.current === this) CatalogFilter.current = null;
    this.open = false;
    document.removeEventListener('click', this.onOutside);
    document.removeEventListener('keydown', this.onKey);
  };

  private onOutside = (e: MouseEvent): void => {
    if (!this.contains(e.target as Node)) this.close();
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.close();
  };

  private toggle(value: string): void {
    const next = this.selected.includes(value)
      ? this.selected.filter((v) => v !== value)
      : [...this.selected, value];
    this.change(next);
  }

  /** "Line", or "2" once more than one is ticked - the chip must stay short. */
  private summary(): string {
    if (!this.selected.length) return '';
    if (this.selected.length > 1) return String(this.selected.length);
    return this.options.find((o) => o.value === this.selected[0])?.label ?? '1';
  }

  override render(): TemplateResult {
    const active = this.selected.length > 0;
    const needle = this.query.trim().toLowerCase();
    const shown = needle
      ? this.options.filter((o) => o.label.toLowerCase().includes(needle))
      : this.options;
    return html`
      <div class="cat-filter ${active ? 'is-active' : ''} ${this.open ? 'is-open' : ''}">
        <button
          class="cat-filter-trigger"
          aria-haspopup="listbox"
          aria-expanded=${this.open}
          @click=${(e: Event) => this.toggleOpen(e)}
        >
          ${this.label}${active
            ? html`<span class="cat-filter-value">${this.summary()}</span>`
            : nothing}
          <svg class="cat-filter-caret" width="9" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
            <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        ${active
          ? html`<button
              class="cat-filter-clear"
              title=${`Clear ${this.label.toLowerCase()}`}
              aria-label=${`Clear ${this.label.toLowerCase()}`}
              @click=${(e: Event) => {
                e.stopPropagation();
                this.change([]);
              }}
            >
              ×
            </button>`
          : nothing}
        ${this.open
          ? html`<div class="cat-filter-menu" role="listbox" aria-multiselectable="true">
              ${this.options.length >= SEARCH_FROM
                ? html`<input
                    class="cat-filter-search"
                    type="search"
                    placeholder=${`Find ${this.label.toLowerCase()}…`}
                    autofocus
                    .value=${this.query}
                    @input=${(e: Event) => {
                      this.query = (e.target as HTMLInputElement).value;
                    }}
                  />`
                : nothing}
              ${shown.map((o) => {
                const on = this.selected.includes(o.value);
                return html`<button
                  class="cat-filter-option ${on ? 'is-on' : ''}"
                  role="option"
                  aria-selected=${on}
                  @click=${() => this.toggle(o.value)}
                >
                  <span class="cat-filter-check" aria-hidden="true">${on ? '✓' : ''}</span>
                  <span class="cat-filter-label">${o.label}</span>
                  <span class="cat-filter-count">${o.count}</span>
                </button>`;
              })}
              ${!shown.length
                ? html`<p class="cat-filter-none">No ${this.label.toLowerCase()} match</p>`
                : nothing}
            </div>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'catalog-filter': CatalogFilter;
  }
}
